import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-100 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">你访问的文章不存在或已被删除</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-primary-500 border border-primary-500/30 hover:bg-primary-500/10 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: 'galagame',
    },
  };

  const paragraphs = article.content.split('\n\n');

  return (
    <>
      <PageSEO
        title={`${article.title} - galagame 游戏资讯`}
        description={article.excerpt}
        keywords={`galagame,galagame资讯,${article.category},galagame${article.category}`}
        canonicalUrl={`https://mkgcgcb.cn/article/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '游戏资讯', path: '/articles' },
            { name: article.title },
          ]}
        />

        <article className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-500/20 text-accent-400 mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
                <span>{article.readTime}阅读</span>
              </div>
            </div>

            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-invert max-w-none">
              {paragraphs.map((paragraph, index) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return null;

                if (trimmed.startsWith('## ')) {
                  return (
                    <h2 key={index} className="font-heading text-lg font-bold text-foreground-100 mt-8 mb-3">
                      {trimmed.replace('## ', '')}
                    </h2>
                  );
                }

                if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                  return (
                    <h3 key={index} className="font-heading text-base font-semibold text-foreground-200 mt-6 mb-2">
                      {trimmed.replace(/\*\*/g, '')}
                    </h3>
                  );
                }

                return (
                  <p key={index} className="text-sm text-foreground-400 leading-relaxed mb-4">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/30">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}